import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthProvider";
import { useMe } from "../hooks/useMe";
import { resolveMembership } from "../hooks/useFamilyMembership";
import { AwaitingApprovalPage } from "../routes/AwaitingApprovalPage";

export function RequireAuth({ children }: { children: ReactNode }) {
  const { state } = useAuth();
  const location = useLocation();
  const signedIn = state.status === "signed-in";
  // Hooks must run on every render (before any conditional return below).
  const meQuery = useMe({ enabled: signedIn });

  if (state.status === "loading") {
    return (
      <div className="page">
        <p>Loading…</p>
      </div>
    );
  }

  if (!signedIn) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Hard onboarding gate: users without a first name (phone-OTP sign-ups
  // have no display name at all) are sent to the profile form and can't
  // browse until they save one. The profile page itself must stay
  // reachable or this would redirect forever.
  const needsOnboarding = meQuery.data?.user.needsOnboarding ?? false;
  const onProfilePage = location.pathname.startsWith("/profile");
  if (needsOnboarding && !onProfilePage) {
    return <Navigate to="/profile?onboarding=1" replace />;
  }

  // No membership in the family whose site this is: they have signed up and
  // are waiting on a moderator. Everything downstream is permission-gated on
  // that membership, so without this they'd get an empty shell with no
  // explanation. Gated on isSuccess so the card can't flash while /me is in
  // flight — resolveMembership is undefined for "loading" and for "not a
  // member" alike. /profile stays reachable so they can fix their details
  // while they wait.
  if (meQuery.isSuccess && !resolveMembership(meQuery.data) && !onProfilePage) {
    return <AwaitingApprovalPage me={meQuery.data} />;
  }

  return <>{children}</>;
}
